/* eslint-disable react/jsx-no-target-blank */
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ScaleIcon from '@src/svgs/ScaleIcon';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { numericalInputStyles } from '../../styles/mainStyle';
import UpDownIcon from '../../svgs/UpDownIcon';
import { AspectRatio } from '../../types.ts/video';
import SliderSelect from '../common/PlainSlider';
import { ElementAnimationSettings } from './ElementAnimationSettings';
import { GenericAnimationSettings } from './GenericAnimationSettings';
import ImageFitPropertySelect from './ImageFitPropertySelect';
import { ImagePreset } from './ImagePreset';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';
import { PropertySelect } from './PropertySelect';

import type { ElementState } from '../../renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface ImageSettingsProps {
  activeElement: ElementState;
  editorStore: EditorStoreLike;
  onReplaceImage?: (newUrl: string) => void;
  parentCompositionElement?: ElementState;
}
const DEFAULT_FIT_PROPS = [
  { caption: 'Cover', value: 'cover' },
  { caption: 'Contain', value: 'contain' },
  {
    caption: 'Black Frame',
    value: 'black-frame',
  },
];

export const ImageSettings: React.FC<ImageSettingsProps> = props => {
  const videoCreator = useVideoCreatorStore();
  const { editorStore } = props;
  const [imageFitProps, setImageFitProps] = useState(DEFAULT_FIT_PROPS);

  // Update image element properties using headless editor
  const updateImageProperty = async (propertyUpdates: Record<string, any>) => {
    if (!props.activeElement?.source?.id) return;
    editorStore.editor.commandUpdateElement(
      props.activeElement.source.id,
      propertyUpdates,
    );
  };

  useEffect(() => {
    const aspectRatio = videoCreator.currentVideo?.aspectRatio;
    if (
      aspectRatio === AspectRatio.AR_9_16 ||
      aspectRatio === AspectRatio.AR_1_1
    ) {
      setImageFitProps(imageFitProps.filter(f => f.value !== 'black-frame'));
    } else setImageFitProps(DEFAULT_FIT_PROPS);
  }, [videoCreator.currentVideo?.aspectRatio]);

  return (
    <ImageSettingsContainer>
      <ImagePresetContainer>
        <ImagePreset
          activeElement={props.activeElement}
          url={props.activeElement.source.source}
          onSourceChange={props.onReplaceImage}
          parentCompositionElement={props.parentCompositionElement}
        />
      </ImagePresetContainer>

      <ElementAnimationSettings
        activeElement={props.activeElement}
        editorStore={editorStore}
      />

      <BasicContent>
        <Item>
          <PropertyCaption>Start</PropertyCaption>
          <PropertyNumericalInput
            activeElement={props.activeElement}
            propertyName="time"
            defaultValue="auto"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              await updateImageProperty({ [property]: value });
            }}
          />
        </Item>

        <Item>
          <PropertyCaption>Duration</PropertyCaption>
          <PropertyNumericalInput
            activeElement={props.activeElement}
            propertyName="duration"
            defaultValue="auto"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              // Update composition inner elements' duration using commandUpdateElement
              if (videoCreator.isImageElementComposition(props.activeElement)) {
                for (const e of props.activeElement.elements || []) {
                  editorStore.editor.commandUpdateElement(e.source.id, {
                    duration: parseFloat(value),
                  });
                }
              }

              // Update the main property using headless editor
              await updateImageProperty({ [property]: value });
            }}
          />
        </Item>

        <Item>
          <PropertyCaption>Fit</PropertyCaption>
          <ImageFitPropertySelect
            activeElement={props.activeElement}
            propertyName="fit"
            defaultValue="cover"
            options={imageFitProps}
            editorStore={editorStore}
          />
        </Item>

        <Item>
          <PropertyCaption>Opacity</PropertyCaption>
          <PropertySelect
            activeElement={props.activeElement}
            propertyName="opacity"
            defaultValue="100%"
            options={[
              { caption: '100%', value: '100%' },
              { caption: '80%', value: '80%' },
              { caption: '60%', value: '60%' },
              { caption: '40%', value: '40%' },
              { caption: '20%', value: '20%' },
            ]}
            editorStore={editorStore}
          />
        </Item>

        {props.activeElement.source.type === 'image' && (
          <SliderGroup>
            <SliderItem>
              <PropertyCaption>Horizontal Alignment</PropertyCaption>
              <SliderAndIcon>
                <UpDownIcon rotate={90} />
                <SliderSelect
                  getValue={newValue =>
                    updateImageProperty({ x_alignment: newValue + '%' })
                  }
                  defaultValue={50}
                  currValue={parseFloat(props.activeElement.source.x_alignment)}
                  min={0}
                  max={100}
                  unit="%"
                />
              </SliderAndIcon>
            </SliderItem>
            <SliderItem>
              <PropertyCaption>Vertical Alignment</PropertyCaption>
              <SliderAndIcon>
                <UpDownIcon />
                <SliderSelect
                  getValue={newValue =>
                    updateImageProperty({ y_alignment: newValue + '%' })
                  }
                  defaultValue={50}
                  currValue={parseFloat(props.activeElement.source.y_alignment)}
                  min={0}
                  max={100}
                  unit="%"
                />
              </SliderAndIcon>
            </SliderItem>
            <SliderItem>
              <PropertyCaption>Zoom In/Out</PropertyCaption>
              <SliderAndIcon>
                <ScaleIcon />
                <SliderSelect
                  getValue={newValue =>
                    updateImageProperty({
                      x_scale: newValue + '%',
                      y_scale: newValue + '%',
                    })
                  }
                  defaultValue={100}
                  currValue={
                    parseFloat(props.activeElement.source.x_scale) || 100
                  }
                  min={100}
                  max={150}
                  unit="%"
                />
              </SliderAndIcon>
            </SliderItem>
          </SliderGroup>
        )}
      </BasicContent>

      <GenericAnimationSettings
        activeElement={props.activeElement}
        editorStore={editorStore}
      />
    </ImageSettingsContainer>
  );
};

// const Information = styled.div`
//   margin-top: 20px;
//   color: #a3a5a5;

//   a {
//     color: #a3a5a5;
//   }
// `;

const ImageSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  padding-bottom: 20px;
  box-sizing: border-box;
`;

const BasicContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

const Item = styled.div``;

const SliderAndIcon = styled.div`
  display: flex;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px;
  gap: 5px;
  align-items: center;
  height: 15px;
`;

const SliderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: 1 / -1;
`;

const SliderItem = styled.div``;

const ImagePresetContainer = styled.div`
  margin: 15px 0;
`;
